import { action, makeObservable } from 'mobx';

import { GroupState } from '../3dEngine/core/GroupState';
import { LoadableMeshState } from '../3dEngine/core/LoadableMeshState';
import { SceneState } from '../3dEngine/core/SceneState';
import { Visualization3dData } from '../data/model';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { VisualizationMode } from '../shared/visualization';
import { IFull3dBasedVisualizationState } from './IVisualizationState';
import { Visualization3dBuilder } from './Visualization3dBuilder';

export class VisualizationFull3dState implements IFull3dBasedVisualizationState {
  public readonly mode = VisualizationMode.Full3d;
  private data: Visualization3dData;
  public sceneState: SceneState;
  public group: GroupState;
  public loadingIndicator: LoadingIndicator;

  constructor(data: Visualization3dData, initialComponents: Map<string, string>) {
    makeObservable(this);
    this.data = data;
    this.sceneState = new SceneState();
    this.group = new GroupState('group', []);
    this.sceneState.addObject(this.group);
    this.loadingIndicator = new LoadingIndicator();
    this.initialize(initialComponents);
  }

  initialize(initialComponents: Map<string, string>) {
    this.setComponents(initialComponents);
  }

  @action
  setComponents(components: Map<string, string>): void {
    const selected3dProductComponents = Visualization3dBuilder.buildComponents3d(this.data, components);
    const transformations = Visualization3dBuilder.getActiveTransformations(this.data, components);

    // TODO: same code as in ModularConfiguratorState, refactor
    const inactiveObjects = [...this.group.objects];

    selected3dProductComponents.forEach((component3d) => {
      let sceneObject = this.sceneState.objects.find(
        (x) => x instanceof LoadableMeshState && x.name === component3d.mesh.name,
      ) as LoadableMeshState;

      if (!sceneObject) {
        sceneObject = new LoadableMeshState(component3d.mesh.name, component3d.mesh.url);
        this.group.addObject(sceneObject);
      } else {
        inactiveObjects.remove(sceneObject);
      }

      if (sceneObject && component3d.material?.name && component3d.material?.url) {
        const meshState = sceneObject;
        meshState.setMaterialName(component3d.material.name);
        meshState.setMaterialUrl(component3d.material?.url);
      }

      transformations.forEach((transformationGroup) => {
        if (transformationGroup.parts.includes(sceneObject.name)) {
          sceneObject.setPosition({
            x: sceneObject.position.x + transformationGroup.translation.x,
            y: sceneObject.position.y + transformationGroup.translation.y,
            z: sceneObject.position.z + transformationGroup.translation.z,
          });
        }
      });
    });

    inactiveObjects.forEach((x) => this.group.removeObject(x));
  }

  getResourcesUrls(): string[] {
    const meshes = this.group.objects.filter((x) => x instanceof LoadableMeshState);
    const meshesUrls = meshes.map((x) => x.url);
    const materialsUrls = meshes.map((x) => x.materialUrl);
    return [...meshesUrls, ...materialsUrls].filter((x) => !!x).distinct();
  }
}
